import { observer } from "mobx-react-lite";
import MainLayout from "../MainLayout";
import { useNavigate } from "react-router-dom";
import { VerticalLayout } from "../../common/common-layout";
import { TitleLargeMedium } from "../../common/common-text";
import BTBox from "../../common/components/BTBox";
import { useEffect, useState } from "react";
import Marketing from "../../model/board/Marketing";
import {
    fetchMarketingList,
    fetchMarketingType,
    requestDeleteAllFiles,
    requestDeleteMarketingDocuments,
} from "../../service/BoardService";
import { dialogStore } from "../../store/DialogStore";
import BTImageList from "../../common/components/board/BTImageList";
import { formatDateToYMD } from "../../common/utils/DateUtils";

const MarketingBoard = () => (
    <MainLayout>
        <MarketingBoardBody />
    </MainLayout>
);

export default MarketingBoard;

const MarketingBoardBody = observer(() => {
    const navigate = useNavigate();
    const [marketings, setMarketings] = useState<Marketing[]>([]);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [marketingTypeOptions, setMarketingTypeOptions] = useState<{ label: string; value: string }[]>([]);
    const [selectedMarketingType, setSelectedMarketingType] = useState<string>("all");
    const [sortOptions] = useState<{ label: string; value: 'title' | 'displayDate' | 'createdAt' }[]>([
        { label: '제목', value: 'title' },
        { label: '게시일자', value: 'displayDate' },
        { label: '생성시각', value: 'createdAt' },
        ]);
    const [selectedSort, setSelectedSort] = useState<'title' | 'displayDate' | 'createdAt'>('createdAt');
    const loadData = async () => {
        const result = await fetchMarketingList();
        setMarketings(result);

        const types = await fetchMarketingType();
        setMarketingTypeOptions(types.map(tab => ({
            label: tab.tabName,
            value: tab.tabCode
        })));
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleWriteClick = () => {
        navigate("/board/marketing/add", {
            state: marketingTypeOptions,
        });
    };

    const handleDeleteClick = async (selectedIndexes: number[]) => {
        if (selectedIndexes.length === 0) {
            dialogStore.showDialog({
                title: "알림",
                message: "삭제할 아이템을 선택해주세요.",
                onConfirm: () => {},
            });
            return;
        }

        const deleteConfirmed = await dialogStore.showAsyncDialog({
            title: "경고",
            message: `선택된 ${selectedIndexes.length}개의 홍보자료를 정말로 삭제하시겠습니까?`,
            isCaution: true,
        });

        if (!deleteConfirmed) return;

        try {
            const selectedMarketingIds: string[] = selectedIndexes
                .map((idx) => marketings[idx]?.id)
                .filter((id): id is string => !!id);

            await requestDeleteMarketingDocuments(selectedMarketingIds);

            await Promise.all(
                selectedMarketingIds.map((docId) =>
                    requestDeleteAllFiles("marketing", docId)
                )
            );

            dialogStore.showDialog({
                title: "삭제 완료",
                message: `${selectedMarketingIds.length}개의 홍보자료가 삭제되었습니다.`,
                onConfirm: () => {
                    setSelectedRows([]);
                    loadData();
                },
                onClose: () => {
                    setSelectedRows([]);
                    loadData();
                },
            });
        } catch (e) {
            console.error("홍보자료 삭제 실패", e);
            dialogStore.showDialog({
                title: "오류",
                message: `홍보자료 삭제 중 오류가 발생했습니다.`,
                onConfirm: () => {},
                onClose: () => {},
            });
        }
    };

    const handleCardClick = (index: number): void => {
        navigate("/board/marketing/detail", {
            state: {
                marketing: marketings[index],
                marketingTypeOptions: marketingTypeOptions,
            },
        });
    };

    return (
        <VerticalLayout height={"auto"} paddingLeft={36} paddingRight={36}>
            <TitleLargeMedium>홍보 게시판 관리</TitleLargeMedium>
            <BTBox height="40px" />
            <BTImageList
              data={marketings.map((d) => ({
                id: d.id,
                title: d.title,
                imageSrc: d.representImage,
                type: d.marketingType,
                displayDate: d.postDate ? formatDateToYMD(d.postDate) : '-',
                isPublished: d.isPublished,
                createdAt: d.createdAt,
              }))}
              onAddClick={handleWriteClick}
              handleCardClick={handleCardClick}
              onDeleteClick={handleDeleteClick}
              addButtonText="추가"
              deleteButtonText="삭제"
              checkboxSelection={true}
              filterTitle="홍보 유형"
              filterOptions={[{ label: '전체', value: 'all' }, ...marketingTypeOptions]}
              filterValue={selectedMarketingType}
              onFilterChange={setSelectedMarketingType}
              sortTitle="정렬"
              sortOptions={sortOptions}
              sortValue={selectedSort}
              onSortChange={setSelectedSort}
              pageStateKey="board.marketing.list"
            />
        </VerticalLayout>
    );
});